import { AlertTriangle, Inbox, ShieldAlert } from 'lucide-react';
import type { ComponentProps, ReactNode } from 'react';

import { ErreurApi } from '@/shared/api/erreurs';
import { cn } from '@/shared/lib/utils';

/* ═══════════════════════ Surfaces ═══════════════════════ */

export function Carte({ className, ...reste }: ComponentProps<'div'>) {
  return (
    <div
      className={cn('rounded-[--radius-astro] border border-bordure bg-surface', className)}
      {...reste}
    />
  );
}

export function EnTeteSection({
  titre,
  description,
  actions,
  sensible,
}: {
  titre: string;
  description?: string;
  actions?: ReactNode;
  /** Marque l'ecran comme journalise ou exposant des donnees personnelles (§0.3). */
  sensible?: string;
}) {
  return (
    <header className="space-y-2">
      <div className="flex flex-wrap items-end justify-between gap-3">
        <div>
          <h1 className="text-lg font-semibold tracking-tight text-texte">{titre}</h1>
          {description ? <p className="mt-0.5 text-texte-doux">{description}</p> : null}
        </div>

        {actions ? <div className="flex flex-wrap gap-2">{actions}</div> : null}
      </div>

      {/*
        Bandeau d'ecran sensible.
        L'exploitant doit savoir en permanence s'il laisse une trace : c'est ce qui
        distingue un acces encadre d'un god mode silencieux (§0.3).
      */}
      {sensible ? (
        <p className="bandeau-sensible flex items-start gap-2 rounded-r-[--radius-astro] px-3 py-2">
          <ShieldAlert className="mt-0.5 size-3.5 shrink-0 text-sensible" aria-hidden />
          <span className="text-texte">{sensible}</span>
        </p>
      ) : null}
    </header>
  );
}

const TONS = {
  neutre: 'border-bordure bg-surface-haute text-texte-doux',
  accent: 'border-accent/30 bg-accent-voile text-accent',
  /* Lavande de la famille Komett, pas un second accent — voir la charte. */
  violet: 'border-violet/30 bg-violet-voile text-violet',
  succes: 'border-succes/30 bg-succes-voile text-succes',
  alerte: 'border-alerte/30 bg-alerte-voile text-alerte',
  danger: 'border-danger/30 bg-danger-voile text-danger',
  sensible: 'border-sensible/40 bg-sensible-voile text-sensible',
};

export type TonPastille = keyof typeof TONS;

export function Pastille({
  ton = 'neutre',
  className,
  ...reste
}: ComponentProps<'span'> & { ton?: TonPastille }) {
  return (
    <span
      className={cn(
        'inline-flex items-center gap-1 rounded-full border px-1.5 py-px',
        'text-[0.6875rem] font-medium whitespace-nowrap',
        TONS[ton],
        className,
      )}
      {...reste}
    />
  );
}

/* ═══════════════════════ Table ═══════════════════════ */

export interface ColonneTable<T> {
  readonly cle: string;
  readonly entete: string;
  /** `true` pour les colonnes de nombres : alignement a droite. */
  readonly numerique?: boolean;
  /** Masquee sous `md`. Sert a garder la table lisible sur un ecran etroit. */
  readonly secondaire?: boolean;
  readonly rendu: (ligne: T) => ReactNode;
}

/**
 * Table dense.
 *
 * Composant unique pour tous les ecrans de liste, avec ses trois etats integres —
 * chargement, erreur, vide (§5). Les factoriser ici evite qu'un ecran oublie l'un des
 * trois et laisse une zone blanche sans explication.
 *
 * Le squelette a la HAUTEUR DES VRAIES LIGNES : sans cela, l'arrivee des donnees fait
 * sauter la page sous le curseur, et on clique la mauvaise ligne — ce qui, sur cet outil,
 * suspend la mauvaise entreprise.
 */
export function Table<T>({
  colonnes,
  lignes,
  cleLigne,
  chargement,
  erreur,
  onReessayer,
  messageVide = 'Aucun resultat.',
  onLigneCliquee,
}: {
  colonnes: readonly ColonneTable<T>[];
  lignes: readonly T[] | undefined;
  cleLigne: (ligne: T) => string;
  chargement?: boolean;
  erreur?: unknown;
  onReessayer?: () => void;
  messageVide?: string;
  onLigneCliquee?: (ligne: T) => void;
}) {
  if (erreur) {
    return <EtatErreur erreur={erreur} onReessayer={onReessayer} />;
  }

  return (
    <Carte className="overflow-hidden">
      <div className="overflow-x-auto">
        <table className="w-full border-collapse text-left">
          <thead>
            <tr className="border-b border-bordure bg-surface-haute/50">
              {colonnes.map((colonne) => (
                <th
                  key={colonne.cle}
                  scope="col"
                  className={cn(
                    'px-3 py-1.5 text-[0.6875rem] font-semibold uppercase tracking-wide text-texte-faible',
                    colonne.numerique && 'text-right',
                    colonne.secondaire && 'hidden md:table-cell',
                  )}
                >
                  {colonne.entete}
                </th>
              ))}
            </tr>
          </thead>

          <tbody>
            {chargement ? (
              Array.from({ length: 8 }, (_, index) => (
                <tr key={`squelette-${index}`} className="border-b border-bordure">
                  {colonnes.map((colonne) => (
                    <td key={colonne.cle} className="px-3 py-2">
                      <div className="h-3 w-full max-w-32 animate-pulse rounded bg-surface-haute" />
                    </td>
                  ))}
                </tr>
              ))
            ) : (lignes?.length ?? 0) === 0 ? (
              <tr>
                <td colSpan={colonnes.length} className="px-3 py-12 text-center">
                  <Inbox className="mx-auto mb-2 size-5 text-texte-faible" aria-hidden />
                  <p className="text-texte-doux">{messageVide}</p>
                </td>
              </tr>
            ) : (
              (lignes ?? []).map((ligne) => (
                <tr
                  key={cleLigne(ligne)}
                  onClick={onLigneCliquee ? () => onLigneCliquee(ligne) : undefined}
                  className={cn(
                    'rangee-anime border-b border-bordure last:border-0',
                    onLigneCliquee && 'cursor-pointer hover:bg-surface-haute/60',
                  )}
                >
                  {colonnes.map((colonne) => (
                    <td
                      key={colonne.cle}
                      className={cn(
                        'px-3 py-1.5 align-middle',
                        colonne.numerique && 'text-right tabular-nums',
                        colonne.secondaire && 'hidden md:table-cell',
                      )}
                    >
                      {colonne.rendu(ligne)}
                    </td>
                  ))}
                </tr>
              ))
            )}
          </tbody>
        </table>
      </div>
    </Carte>
  );
}

/**
 * Etat d'erreur.
 *
 * Distingue le refus d'acces du reste : un 403 sur cet outil n'est pas une panne, c'est
 * le refus par defaut qui fonctionne (§3). Le presenter comme une erreur technique
 * enverrait chercher un probleme la ou il n'y en a pas.
 */
export function EtatErreur({ erreur, onReessayer }: { erreur: unknown; onReessayer?: () => void }) {
  const api = erreur instanceof ErreurApi ? erreur : null;
  const refus = api?.estRefusAcces ?? false;

  return (
    <Carte className="flex flex-col items-center gap-2 p-8 text-center">
      <AlertTriangle
        className={cn('size-5', refus ? 'text-alerte' : 'text-danger')}
        aria-hidden
      />

      <p className="font-medium text-texte">
        {refus ? 'Acces refuse par l’API' : 'La requete a echoue'}
      </p>

      <p className="max-w-md text-texte-doux">
        {refus
          ? 'Ce compte n’a pas les droits d’administration de plateforme pour cette ressource.'
          : (api?.message ?? 'Une erreur inattendue est survenue.')}
      </p>

      {api?.correlationId ? (
        <p className="text-texte-faible">
          Correlation <code>{api.correlationId}</code>
        </p>
      ) : null}

      {onReessayer && api?.estTransitoire ? (
        <button
          type="button"
          onClick={onReessayer}
          className="mt-1 rounded-[--radius-astro] border border-bordure bg-surface-haute px-3 py-1 text-texte transition-colors duration-100 hover:bg-surface-survol"
        >
          Reessayer
        </button>
      ) : null}
    </Carte>
  );
}

/** Tuile de chiffre pour le tableau de bord. */
export function Tuile({
  libelle,
  valeur,
  detail,
  ton,
}: {
  libelle: string;
  valeur: ReactNode;
  detail?: string;
  ton?: TonPastille;
}) {
  return (
    <Carte className="p-3">
      <p className="text-[0.6875rem] uppercase tracking-wide text-texte-faible">{libelle}</p>

      <p
        className={cn(
          'mt-1 text-2xl font-semibold tabular-nums',
          ton === 'danger' ? 'text-danger' : ton === 'alerte' ? 'text-alerte' : 'text-texte',
        )}
      >
        {valeur}
      </p>

      {detail ? <p className="mt-0.5 text-texte-faible">{detail}</p> : null}
    </Carte>
  );
}
